import { useLocation, useRoute } from "wouter";
import { Plus, Settings as SettingsIcon } from "lucide-react";
import AppShell from "@/components/foundations/core/AppShell/AppShell";
import { Button } from "@/components/ui/button";
import { trpc } from "@/lib/trpc";
import { DEFAULT_AREA_COLOR, AREA_COLORS, ListColorKey } from "@/lib/listColors";
import { formatDateLong, parseDateKey } from "@/lib/time";

export default function AddList() {
  const [, params] = useRoute<{ date: string }>("/day/:date/add");
  const [, setLocation] = useLocation();
  const date = params?.date ?? "";

  const listsQuery = trpc.lists.list.useQuery();

  const dateLabel = date ? formatDateLong(parseDateKey(date)) : "";
  const lists = (listsQuery.data ?? []).filter(l => l.isGlobalActive);

  return (
    <AppShell backTo={`/calendar?day=${date}`} title="Add list to day">
      <div className="container max-w-4xl py-8 space-y-8">
        <header className="space-y-2">
          <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
            {dateLabel}
          </p>
          <h1 className="font-display text-3xl tracking-tight">
            Choose a list
          </h1>
          <p className="text-sm text-muted-foreground max-w-prose">
            Pick a list to configure its tasks for this day. You can adjust
            start times, duration, repeats, and alerts before adding.
          </p>
        </header>

        {listsQuery.isLoading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[0, 1, 2, 3].map(i => (
              <div key={i} className="h-32 rounded-2xl bg-muted/40 animate-pulse" />
            ))}
          </div>
        ) : lists.length === 0 ? (
          <div className="border border-dashed border-border rounded-2xl p-12 text-center space-y-4">
            <p className="font-display text-xl">No active lists</p>
            <p className="text-sm text-muted-foreground">
              Create a list in settings before adding tasks to a day.
            </p>
            <Button onClick={() => setLocation("/settings/lists")} className="gap-1.5">
              <SettingsIcon className="h-4 w-4" />
              Open settings
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {lists.map(l => {
              const c = AREA_COLORS[l.color as ListColorKey] ?? AREA_COLORS[DEFAULT_AREA_COLOR];
              return (
                <button
                  key={l.id}
                  onClick={() => setLocation(`/day/${date}/add/${l.id}`)}
                  className="group relative bg-card border border-border/60 rounded-2xl p-5 text-left transition-all hover:border-foreground/30 hover:shadow-sm"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2.5 mb-2">
                        <span
                          className="h-7 w-7 rounded-full flex items-center justify-center font-display text-xs"
                          style={{ background: c.soft, color: c.ink }}
                        >
                          {l.priorityNumber}
                        </span>
                        <h3 className="font-display text-lg tracking-tight truncate">
                          {l.name}
                        </h3>
                      </div>
                      {l.description && (
                        <p className="text-sm text-muted-foreground line-clamp-2 leading-relaxed">
                          {l.description}
                        </p>
                      )}
                      <div className="mt-3 text-[11px] uppercase tracking-wider text-muted-foreground">
                        {l.taskCount} {l.taskCount === 1 ? "task" : "tasks"}
                      </div>
                    </div>
                    <span
                      className="h-3 w-3 rounded-full shrink-0 mt-1"
                      style={{ background: c.base }}
                    />
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </AppShell>
  );
}
